import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Button,
  message,
  Spin,
  Switch,
  Alert,
} from "antd";
import styled from "styled-components";
import { useAuth } from "../../contexts/AuthContext";
import moment from "moment";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const { TextArea } = Input;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
  }
`;

const FormSection = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h5`
  font-weight: 600;
  color: #3c2f61;
  margin-bottom: 10px;
`;

const EditEventModal = ({ visible, event, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [notifyMembers, setNotifyMembers] = useState(false);
  const [changedFields, setChangedFields] = useState([]);

  useEffect(() => {
    if (visible && event) {
      // Set initial form values
      form.setFieldsValue({
        name: event.name,
        description: event.description,
        event_date: event.event_date ? moment(event.event_date) : null,
        amount: event.amount,
        physical_location: event.physical_location,
        payment_info: event.payment_info,
      });
      setChangedFields([]);
    }
  }, [visible, event, form]);

  const handleFieldChange = (fieldName) => {
    if (!changedFields.includes(fieldName)) {
      setChangedFields([...changedFields, fieldName]);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const payload = {
        name: values.name,
        description: values.description,
        event_date: values.event_date ? values.event_date.format("YYYY-MM-DD") : null,
        amount: values.amount,
        physical_location: values.physical_location,
        payment_info: values.payment_info,
        notify_members: notifyMembers,
        changed_fields: changedFields,
      };

      const response = await axios.put(
        `${BASE_URL}/companies/events/${event.id}/update`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200 && response.data.status === "success") {
        message.success("Event updated successfully!");
        form.resetFields();
        setNotifyMembers(false);
        setChangedFields([]);
        onSuccess();
      } else {
        message.error(response.data.message || "Failed to update event");
      }
    } catch (error) {
      console.error("Error updating event:", error);
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error("Failed to update event. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setNotifyMembers(false);
    setChangedFields([]);
    onCancel();
  };

  return (
    <StyledModal
      title="Edit Event Details"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={700}
      destroyOnClose
    >
      <Spin spinning={loading}>
      
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <FormSection>
          
            
            <Form.Item
              name="name"
              label="Event Name"
              rules={[{ required: true, message: "Please enter event name" }]}
            >
              <Input 
                placeholder="Enter event name" 
                onChange={() => handleFieldChange("name")}
              />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
            >
              <TextArea
                rows={4}
                placeholder="Enter event description"
                onChange={() => handleFieldChange("description")}
              />
            </Form.Item>

            <Form.Item
              name="event_date"
              label="Event Date"
              rules={[{ required: true, message: "Please select event date" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                onChange={() => handleFieldChange("event_date")}
              />
            </Form.Item>

            <Form.Item
              name="physical_location"
              label="Physical Location"
            >
              <Input 
                placeholder="Enter event location"
                onChange={() => handleFieldChange("physical_location")}
              />
            </Form.Item>
          </FormSection>

          <FormSection>
            <SectionTitle>Pricing & Payment</SectionTitle>
            
            <Form.Item
              name="amount"
              label="Amount (per person)"
              rules={[
                { required: true, message: "Please enter amount" },
                { type: "number", min: 0, message: "Amount must be positive" }
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Enter amount"
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                onChange={() => handleFieldChange("amount")}
              />
            </Form.Item>

            <Form.Item
              name="payment_info"
              label="Payment Information"
            >
              <TextArea
                rows={3}
                placeholder="Enter payment instructions or details"
                onChange={() => handleFieldChange("payment_info")}
              />
            </Form.Item>
          </FormSection>

          {/* <FormSection>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
              <SectionTitle style={{ marginBottom: 0, marginRight: "10px" }}>
                Notify Members About Changes
              </SectionTitle>
              <Switch
                checked={notifyMembers}
                onChange={setNotifyMembers}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            </div>
            {notifyMembers && (
              <Alert
                message="Members will be notified about the changes you made"
                type="info"
                showIcon
              />
            )}
          </FormSection> */}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
            <Button onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{ backgroundColor: "#3c2f61", borderColor: "#3c2f61" }}
            >
              Update Event
            </Button>
          </div>
        </Form>
      </Spin>
    </StyledModal>
  );
};

export default EditEventModal;